import { reportAuthFailure } from '@/services/authFailureLog'
import { useState, useEffect, FormEvent, ChangeEvent } from 'react'
import type { MultiFactorError } from 'firebase/auth'
import { SecondStepChallenge } from './SecondStepChallenge'
import { secondStepAccount } from '@/services/twoFactor'
import {
  signInWithEmail,
  signInWithGoogle,
  signInWithApple,
  updateLastLogin,
} from '../../services/auth'
import { useNavigate, useLocation } from 'react-router-dom'
import { TextLink } from '@/components/ui/text-link'
import { useSession } from '../../contexts/SessionContext'
import { Button } from '../ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Icon } from '../ui/icon'
import { faCircleExclamation } from '@awesome.me/kit-e215ba4646/icons/sharp/light'
import { APPLE_SIGN_IN_ENABLED } from '@/constants/authProviders'
import { NoInvitationNotice } from './NoInvitationNotice'
import { takeRefusedEmail } from '@/services/auth/inviteOnly'
import { AuthPageFrame, AuthDivider } from './AuthPageFrame'
import { GoogleMark, AppleMark } from './ProviderMarks'
import { Spinner } from '@/components/ui/spinner'

interface SignInProps {
  redirectPath?: string
}

const SignIn = ({ redirectPath = '/' }: SignInProps) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const location = useLocation()
  // Set when Google / Apple sign-in was refused for no invitation (PB-64).
  // Kept in this page's history entry, so Back from sign-up still shows it.
  const [refusedEmail, setRefusedEmail] = useState<string | null>(
    () =>
      (location.state as { refusedEmail?: string } | null)?.refusedEmail ??
      takeRefusedEmail()
  )
  const [loading, setLoading] = useState(false)
  // Set when Firebase asks for the second sign-in step (PB-62)
  const [secondStep, setSecondStep] = useState<MultiFactorError | null>(null)
  useEffect(() => {
    if (!secondStep) return
    const a = secondStepAccount(secondStep)
    // PB-81 diagnostics: does the second-step error say who is signing in?
    console.info('[SecondStep] account', {
      hasUid: !!a.uid,
      hasEmail: !!a.email,
    })
  }, [secondStep])
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()

  useEffect(() => {
    const state = (location.state ?? {}) as Record<string, unknown>
    if (refusedEmail === null || state.refusedEmail === refusedEmail) return
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...state, refusedEmail },
    })
  }, [refusedEmail, location, navigate])

  // Defensive cleanup of any DOM state left over from the previous
  // authenticated session. Two known classes of stuck state:
  //
  // 1. Radix UI (Dialog / Popover / Dropdown) sets pointer-events: none +
  //    aria-hidden on <body> when modal-locked. If the host component
  //    unmounts while the dialog is in open state (e.g. SidebarLayout
  //    tears down on sign-out before the dialog's onOpenChange fires),
  //    Radix's cleanup effect doesn't run and the body styles persist.
  //    Result: the SignIn page renders behind a body with no pointer
  //    events — in a PWA with no browser chrome, this traps the user
  //    until they force-quit the app (D-N-070).
  // 2. Vaul (mobile Drawer mode of ResponsiveDialog) sets
  //    data-scroll-locked + overflow: hidden on html for scroll locking.
  //    Same unmount-while-open race leaves the document non-scrollable.
  //
  // We can't reliably close every possible open dialog at sign-out time
  // because dialog state lives in hooks scattered across the tree. The
  // simplest fix is to clear the known styles + attributes on SignIn
  // mount unconditionally — if nothing is stuck, this is a no-op; if
  // something is, the user is unblocked immediately.
  useEffect(() => {
    document.body.style.removeProperty('pointer-events')
    document.body.style.removeProperty('overflow')
    document.body.removeAttribute('aria-hidden')
    document.body.removeAttribute('data-scroll-locked')
    document.documentElement.style.removeProperty('overflow')
  }, [])

  // Silent cache cleanup on /signin mount. Clears every cache storage
  // entry without unregistering the service worker — that distinction
  // is critical:
  //
  // - The SW is what serves cached HTML/JS. Cached responses are what
  //   makes an old bundle "sticky." Clearing caches.delete() is what
  //   actually breaks the stale-bundle trap; the SW's next intercept
  //   misses cache and falls through to network → fresh bundle.
  // - The SW itself is also what Firebase Messaging attaches the push
  //   subscription to. Unregistering it breaks FCM until the next full
  //   page reload re-registers from index.html. Tenant sees
  //   `InvalidStateError: Getting push subscription requires a service
  //   worker` when toggling push on, even after the toggle handler
  //   reaches getToken().
  //
  // So: clear caches, keep the SW. Best of both — fresh files on next
  // fetch, and push notifications continue to function.
  useEffect(() => {
    void (async () => {
      try {
        if ('caches' in window) {
          const names = await caches.keys()
          await Promise.all(names.map(name => caches.delete(name)))
        }
      } catch {
        // Best-effort. If caches API is unavailable, sign-in still
        // works from the already-loaded bundle.
      }
    })()
  }, [])

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate(redirectPath)
  }, [isAuthenticated, navigate, redirectPath])

  const handleEmailSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')
    setRefusedEmail(null)
    setLoading(true)
    try {
      const result = await signInWithEmail(email, password)
      if (result.success) navigate(redirectPath)
      else if (result.secondStep) setSecondStep(result.secondStep)
      else {
        reportAuthFailure(
          'password_sign_in',
          { code: result.code, message: result.error },
          { email }
        )
        setError(result.error || 'Failed to sign in')
      }
    } catch (err) {
      reportAuthFailure('password_sign_in', err, { email })
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to sign in: ' + message)
    }
    setLoading(false)
  }

  // No page lock while the Google window is open (D103): Firebase only
  // notices a closed window ~10 s later (the COOP warning), so the buttons
  // stay usable and pressing one again replaces the window.
  const handleGoogleSignIn = async () => {
    setError('')
    setRefusedEmail(null)
    try {
      const result = await signInWithGoogle()
      if (result.success) navigate(redirectPath)
      else if (result.secondStep) setSecondStep(result.secondStep)
      else if (result.noInvitation) setRefusedEmail(result.noInvitation.email)
      else if (result.cancelled) return
      else {
        reportAuthFailure('google_sign_in', {
          code: result.code,
          message: result.error,
        })
        setError(result.error || 'Failed to sign in with Google')
      }
    } catch (err) {
      reportAuthFailure('google_sign_in', err)
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to sign in with Google: ' + message)
    }
  }

  // Same as Google: no page lock while the window is open (D103)
  const handleAppleSignIn = async () => {
    setError('')
    setRefusedEmail(null)
    try {
      const result = await signInWithApple()
      if (result.success) navigate(redirectPath)
      else if (result.secondStep) setSecondStep(result.secondStep)
      else if (result.noInvitation) setRefusedEmail(result.noInvitation.email)
      else if (result.cancelled) return
      else {
        reportAuthFailure('apple_sign_in', {
          code: result.code,
          message: result.error,
        })
        setError(result.error || 'Failed to sign in with Apple')
      }
    } catch (err) {
      reportAuthFailure('apple_sign_in', err)
      const message = err instanceof Error ? err.message : 'Unknown error'
      setError('Failed to sign in with Apple: ' + message)
    }
  }

  return (
    <AuthPageFrame>
      {refusedEmail !== null && <NoInvitationNotice email={refusedEmail} />}

      {error && (
        <Alert variant="destructive">
          <Icon icon={faCircleExclamation} className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Welcome back</CardTitle>
          <CardDescription>Sign in to your MNTND account</CardDescription>
        </CardHeader>
        <CardContent>
          {secondStep ? (
            <SecondStepChallenge
              error={secondStep}
              onDone={credential => {
                updateLastLogin(credential.user.uid)
                navigate(redirectPath)
              }}
              onCancel={() => setSecondStep(null)}
              recoveryAccount={{
                ...secondStepAccount(secondStep),
                ...(email && { email }),
              }}
            />
          ) : (
            <form onSubmit={handleEmailSignIn}>
              <div className="grid gap-6">
                {/* OAuth providers */}
                <div className="flex flex-col gap-4">
                  {APPLE_SIGN_IN_ENABLED && (
                    <Button
                      variant="outline"
                      className="w-full"
                      type="button"
                      disabled={loading}
                      onClick={handleAppleSignIn}
                    >
                      <AppleMark />
                      Login with Apple
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="w-full"
                    type="button"
                    disabled={loading}
                    onClick={handleGoogleSignIn}
                  >
                    <GoogleMark />
                    Login with Google
                  </Button>
                </div>

                {/* Divider */}
                <AuthDivider label="Or continue with" />

                {/* Email / password */}
                <div className="grid gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={loading}
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                      }
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center">
                      <Label htmlFor="password">Password</Label>
                      <TextLink
                        to="/forgot-password"
                        className="ml-auto text-sm"
                      >
                        Forgot your password?
                      </TextLink>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="current-password"
                      disabled={loading}
                      value={password}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner className="mr-2" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </div>

                <div className="text-center text-sm">
                  Don&apos;t have an account?{' '}
                  <TextLink to="/signup">Sign up</TextLink>
                </div>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </AuthPageFrame>
  )
}

export default SignIn
